/**
 * SkillView — índice invertido SKILL → agentes que a utilizam (§10/§12 do handoff).
 *
 * Reflete o registry real (GET /agents já traz `skills[]` por agente), então é
 * uma projeção 100% no front — nenhum endpoint novo. Cross-navigation: clicar num
 * agente foca-o no Mission Control (AgentRoom). Paleta C, pt-BR, a11y.
 */
import { useMemo, useState } from 'react';
import { Search, Wrench, Users } from 'lucide-react';
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useAgents } from '@/api/agents';

export function SkillView({ onFocusAgent }) {
  // O endpoint /agents limita a 200 (ge=1, le=200) — pedir mais dava 422.
  const { data, isLoading, error, refetch } = useAgents({ limit: 200 });
  const [q, setQ] = useState('');

  const skillIndex = useMemo(() => {
    const items = data?.items ?? [];
    const map = new Map(); // skill -> Set(agentName)
    for (const a of items) {
      for (const s of a.skills ?? []) {
        if (!map.has(s)) map.set(s, new Set());
        map.get(s).add(a.name || a.slug || a.id);
      }
    }
    return Array.from(map.entries())
      .map(([skill, agents]) => ({ skill, agents: Array.from(agents).sort() }))
      .sort((x, y) => y.agents.length - x.agents.length || x.skill.localeCompare(y.skill));
  }, [data]);

  if (isLoading) return <LoadingSpinner label="Carregando skills do registry..." />;
  if (error) return <QueryError error={error} onRetry={() => refetch()} />;

  const term = q.trim().toLowerCase();
  const rows = term
    ? skillIndex.filter((r) => r.skill.toLowerCase().includes(term)
        || r.agents.some((a) => a.toLowerCase().includes(term)))
    : skillIndex;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.neutral }}>
          Skills do registry
        </span>
        <span style={{ fontSize: 11, color: C.neutralMuted }}>
          {skillIndex.length} skills · {data?.total ?? (data?.items?.length ?? 0)} agentes
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
          background: C.blackElev, border: `1px solid ${C.blackBorder}`, borderRadius: 8,
          padding: '6px 10px' }}>
          <Search size={14} style={{ color: C.neutralMuted }} aria-hidden="true" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filtrar por skill ou agente…"
            aria-label="Filtrar skills"
            style={{ background: 'transparent', border: 'none', outline: 'none',
              color: C.neutral, fontSize: 13, width: 220 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map(({ skill, agents }) => (
          <div key={skill} style={{ padding: '10px 12px', background: C.blackElev,
            border: `1px solid ${C.blackBorder}`, borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Wrench size={13} style={{ color: C.cyber }} aria-hidden="true" />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.neutral }}>{skill}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11,
                color: C.neutralMuted, marginLeft: 4 }}>
                <Users size={11} aria-hidden="true" /> {agents.length}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {agents.map((name) => (
                <button
                  key={name}
                  onClick={() => onFocusAgent?.({ id: name, name })}
                  title={`Focar ${name}`}
                  style={{ fontSize: 11.5, color: C.cyber, background: `${C.cyber}12`,
                    border: `1px solid ${C.cyber}33`, borderRadius: 12, padding: '2px 9px',
                    cursor: 'pointer' }}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        ))}
        {!rows.length && (
          <div style={{ padding: 24, color: C.neutralMuted, textAlign: 'center' }}>
            Nenhuma skill encontrada para “{q}”.
          </div>
        )}
      </div>
    </div>
  );
}
